import { useEffect, useRef } from 'react';
import { useGame } from '../state/useGame';

/**
 * Audio engine: plays SFX on game events, loops BGM.
 * All assets are CC0 (Kenney.nl + OpenGameArt) per CREDITS.md.
 */
export function AudioEngine() {
  const state = useGame((s) => s.state);
  const settings = state.settings;

  const sfxRef = useRef<Record<string, HTMLAudioElement | null>>({
    'ui-click': null,
    'level-up': null,
    'combat-hit': null,
    'monster-kill': null,
    'skill-train': null,
  });

  const bgmRef = useRef<HTMLAudioElement | null>(null);
  const prevSkillsRef = useRef(state.skills);
  const prevActiveRef = useRef(state.active);

  // Wire SFX elements once
  useEffect(() => {
    const map: Record<string, HTMLAudioElement | null> = {};
    for (const k of ['ui-click', 'level-up', 'combat-hit', 'monster-kill', 'skill-train']) {
      const a = new Audio(`/assets/audio/${k}.ogg`);
      a.preload = 'auto';
      a.volume = settings.soundVolume;
      map[k] = a;
    }
    sfxRef.current = map;

    const bgm = new Audio('/assets/audio/bgm-loop.mp3');
    bgm.loop = true;
    bgm.volume = settings.musicVolume;
    bgmRef.current = bgm;

    const removeUnlockListeners = () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
    const unlockAudio = () => {
      if (bgm.volume <= 0 || !bgm.paused) return;
      const attempt = bgm.play();
      if (attempt) void attempt.then(removeUnlockListeners).catch(() => undefined);
      else removeUnlockListeners();
    };
    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio);

    if (settings.musicVolume > 0) {
      void bgm.play()?.catch(() => { /* autoplay blocked; user must interact */ });
    }
    return () => {
      removeUnlockListeners();
      bgm.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to level-ups
  useEffect(() => {
    const prev = prevSkillsRef.current;
    for (const [id, prog] of Object.entries(state.skills)) {
      const before = prev[id as keyof typeof prev];
      if (before && prog.level > before.level) {
        const sfx = sfxRef.current['level-up'];
        if (sfx) {
          sfx.currentTime = 0;
          void sfx.play()?.catch(() => {});
        }
      }
    }
    prevSkillsRef.current = state.skills;
  }, [state.skills]);

  // React to combat start (active changed to combat)
  useEffect(() => {
    const prevKind = prevActiveRef.current.kind;
    if (state.active.kind === 'combat' && prevKind !== 'combat') {
      const sfx = sfxRef.current['ui-click'];
      if (sfx) {
        sfx.currentTime = 0;
        void sfx.play()?.catch(() => {});
      }
    }
    prevActiveRef.current = state.active;
  }, [state.active]);

  // React to volume changes
  useEffect(() => {
    for (const a of Object.values(sfxRef.current)) {
      if (a) a.volume = settings.soundVolume;
    }
    if (bgmRef.current) {
      bgmRef.current.volume = settings.musicVolume;
      if (settings.musicVolume === 0 && !bgmRef.current.paused) bgmRef.current.pause();
      else if (settings.musicVolume > 0 && bgmRef.current.paused) void bgmRef.current.play()?.catch(() => {});
    }
  }, [settings.soundVolume, settings.musicVolume]);

  return null;
}